import {createAbortControl} from '@raison/toolbox';
import {useMemo} from 'react';
import {env} from '~/environment';
import {isMobileApp} from '../utils/mobileDeviceDetect';

interface JsEventRegistry {
    PermissionSettingsIntent: void;
    ShellVersionRequest: void;
    ShellAppUpdateIntent: void;
}

interface ShellEventRegistry {
    ShellVersionResponse: {version: string};
}

export namespace ShellEventBus {
    type JsEventType = keyof JsEventRegistry;
    type ShellEventType = keyof ShellEventRegistry;

    const EVENT_NAME = 'raison:event';

    export function supports(eventType: JsEventType): boolean {
        return window.appSupportedEvents?.includes(eventType) || false;
    }

    export function sendIfCan<K extends JsEventType>(eventType: K, eventData?: JsEventRegistry[K]) {
        if (isMobileApp() && supports(eventType)) {
            return send(eventType, eventData);
        }
    }

    export function send<K extends JsEventType>(eventType: K, eventData?: JsEventRegistry[K]) {
        const dto = new URLSearchParams({
            type: eventType,
            ...(!eventData ? null : {data: btoa(JSON.stringify(eventData))}),
        });
        const url = new URL(`/${EVENT_NAME}?${dto}`, env.APP_URL);
        open(url);

        return {
            andWaitFor<K extends ShellEventType>(eventType: K) {
                return new Promise<ShellEventRegistry[K]>(resolve => {
                    const unsubscribe = subscribe(eventType, data => {
                        unsubscribe();
                        resolve(data);
                    });
                });
            },
        };
    }

    export function emulateShellEvent<K extends keyof ShellEventRegistry>(
        eventType: K,
        eventData?: ShellEventRegistry[K]
    ) {
        window.dispatchEvent(new CustomEvent(EVENT_NAME, {detail: {type: eventType, data: eventData}}));
    }

    export function subscribeToAll(callback: (eventType: string, data: unknown) => void) {
        const [signal, abort] = createAbortControl();
        window.addEventListener(EVENT_NAME, handleEvent, {signal});
        return abort;

        function handleEvent(ev: Event) {
            if (!(ev instanceof CustomEvent)) {
                return;
            }
            callback(ev.detail.type, ev.detail.data);
        }
    }

    export function subscribe<K extends ShellEventType>(eventType: K, callback: (data: ShellEventRegistry[K]) => void) {
        return subscribeToAll((type, data) => {
            if (type === eventType) {
                callback(data as any);
            }
        });
    }

    export function useSendEvent<K extends JsEventType>(eventType: K) {
        return useMemo(() => {
            if (!ShellEventBus.supports(eventType)) {
                return null;
            }
            return (eventData: JsEventRegistry[K]) => {
                return ShellEventBus.send(eventType, eventData);
            };
        }, [eventType]);
    }
}
